import supercluster from 'supercluster'

import { pendingSelector } from 'api/selectors'
import { entitySelector } from 'wordpress/selectors'
import { searchDateTimeSelector } from 'search-date-time'
import { createDenormalizedSelector } from 'entities/selectors'
import { locationSchema, POIScheme } from 'sfproxy/schemas'
import { rateSchema } from 'grs/schemas'
import { languageSelector } from 'i18n/selectors'

import { SEARCH_MODE_MONTHLY, SEARCH_MODE_HOURLY } from 'search/constants'
import { Feature } from './helpers'
import { isHostCanada } from 'shared/country'
import featureToggles from 'shared/featureToggles'
import { CURRENT_COUNTRY_CANADA } from 'shared/constants'

const stateSelector = state => state.map

/* Rates */

export const ratesStateSelector = createSelector(stateSelector, state => state.rates)

export const ratesResultsSelector = createSelector(ratesStateSelector, state => (state instanceof Array ? state : []))
export const ratesErrorSelector = createSelector(ratesStateSelector, state => (state instanceof Error ? state : null))

const ratesSelector = createDenormalizedSelector(ratesResultsSelector, [rateSchema])

export const ratesByGrsIdSelector = createSelector(ratesSelector, rates =>
    rates.reduce((map, rate) => {
        if (map[rate.eDataLocationId] instanceof Array) map[rate.eDataLocationId].push(rate)
        else map[rate.eDataLocationId] = [rate]
        return map
    }, {})
)

/* Results */

export const resultsStateSelector = createSelector(stateSelector, state => state.results)

const activeLocationIdSelector = createSelector(stateSelector, state => state.activeLocation)
const hoveredLocationIdSelector = createSelector(stateSelector, state => state.hoveredLocation)

export const resultsSelector = createSelector(resultsStateSelector, state => state.results)
const inactiveResultsSelector = createSelector(
    resultsStateSelector,
    activeLocationIdSelector,
    (state, activeLocationId) => state.results.filter(value => value !== activeLocationId)
)

export const resultsPlacesOfInterestSelector = createSelector(resultsStateSelector, state => state.poi)
export const resultsCountSelector = createSelector(resultsStateSelector, state => state.resultsCount)

const inactiveLocationsSelector = createDenormalizedSelector(inactiveResultsSelector, [locationSchema])
export const placesOfInterestSelector = createDenormalizedSelector(resultsPlacesOfInterestSelector, [POIScheme])

/* Other simple selectors */

export const googleAPIReadySelector = createSelector(stateSelector, state => state.apiReady)
export const maximumMapBoundsSelector = createSelector(stateSelector, state => state.maxBounds)
export const boundsSelector = createSelector(stateSelector, state => state.bounds)
export const filtersSelector = createSelector(stateSelector, state => state.filters)
export const resultsModeSelector = createSelector(stateSelector, state => state.resultsMode)
export const searchModeSelector = createSelector(stateSelector, state => state.searchMode)
export const searchLocationSelector = createSelector(stateSelector, state => state.searchLocation)
export const zoomSelector = createSelector(stateSelector, state => state.zoom)
export const centerSelector = createSelector(stateSelector, state => state.center)
export const scrollIntoViewSelector = createSelector(stateSelector, state => state.scrollIntoView)

export const activeLocationSelector = createDenormalizedSelector(activeLocationIdSelector, locationSchema)

export const activeSearchLocationSelector = createSelector(stateSelector, state => state.activeSearchLocation)

/* Location */

//TODO There is identical selectors in src/sfproxy, these should be unified.
const idPropSelector = (state, props) => props.sfLocationId
export const locationSelector = createDenormalizedSelector(idPropSelector, locationSchema)
export const placeOfInterestSelector = createDenormalizedSelector(idPropSelector, POIScheme)

const rateIdsSelector = createSelector(
    locationSelector,
    ratesByGrsIdSelector,
    (location, ratesByGrsId) => ratesByGrsId[location.grsId] || []
)
export const locationRatesSelector = createDenormalizedSelector(rateIdsSelector, [rateSchema])

export const canBookSelector = createSelector(locationSelector, location =>
    !!location.grsId && isHostCanada && location.address.country !== CURRENT_COUNTRY_CANADA
        ? false
        : !!location.grsId && location.reservationsEnabled
)

export const canSubscribeSelector = createSelector(locationSelector, location =>
    !!location.grsId && isHostCanada && location.address.country !== CURRENT_COUNTRY_CANADA
        ? false
        : !!location.grsId && location.seasonTicketsEnabled
)

export const canBuySelector = createSelector(
    canBookSelector,
    canSubscribeSelector,
    searchModeSelector,
    (canBook, canSubscribe, searchMode) => (searchMode === SEARCH_MODE_HOURLY ? canBook : canSubscribe)
)

export const buyLabelTranslationSelector = createSelector(searchModeSelector, searchMode =>
    searchMode === SEARCH_MODE_HOURLY ? 'map.results.list.book.now' : 'map.results.list.subscribe'
)

const lowestRateSelector = createSelector(locationRatesSelector, searchModeSelector, (rates, searchMode) =>
    rates
        .filter(
            rate =>
                (rate.isMonthlyPass && searchMode === SEARCH_MODE_MONTHLY) ||
                (!rate.isMonthlyPass && searchMode === SEARCH_MODE_HOURLY)
        )
        .reduce((lowestRate, rate) => (lowestRate ? (rate.amount < lowestRate.amount ? rate : lowestRate) : rate), null)
)
export const rateLabelSelector = createSelector(lowestRateSelector, rate =>
    rate
        ? {
              currency: rate.currencySymbol,
              fractionDigits: rate.amount && rate.amount < 100 ? 2 : 0,
              amount: rate.amount,
          }
        : null
)

export const carParkUrlSelector = createSelector(locationSelector, location => `car-park/${location.id}`)
export const carParPhoneSelector = createSelector(locationSelector, location => location.phone)
export const externalSeasonTicketUrlSelector = createSelector(
    locationSelector,
    location => location.externalSeasonTicketUrl
)

export const bookingUrlSelector = createSelector(
    locationSelector,
    languageSelector,
    searchModeSelector,
    (location, language, searchMode) => {
        const { grsId } = location

        if (searchMode !== SEARCH_MODE_MONTHLY) {
            return `/booking/${grsId}`
        }
        if (!featureToggles.newSubscriptionFlow) {
            return `/booking/${grsId}/subscriptions`
        }
        if (location.migratedLocation || featureToggles.brNeoPurchaseFlow || featureToggles.isColumbia) {
            return `/subscription/${grsId}`
        }

        const urls = window.BOOKING_MONTHLY_URLS || {}
        return urls[language] || window.BOOKING_MONTHLY_DEFAULT_URL || ''
    }
)

export const monthlyParkingUrlSelector = createSelector(locationSelector, location => {
    const { id } = location
    return `/monthly-parking/${id}`
})

export const redirectToMonthlyParkingFlowSelector = createSelector(
    searchModeSelector,
    locationSelector,
    (searchMode, location) =>
        featureToggles.caPurchaseFlow &&
        searchMode === SEARCH_MODE_MONTHLY &&
        location.seasonTicketsEnabled &&
        !location.migratedLocation
)

export const brandSelector = createSelector(
    locationSelector,
    entitySelector('brands'),
    (location, brands) => brands[location.brand]
)

export const isActiveSelector = createSelector(idPropSelector, activeLocationIdSelector, (a, b) => a === b)
export const isHoveredSelector = createSelector(idPropSelector, hoveredLocationIdSelector, (a, b) => a === b)

/* Computed data selectors */

export const loadingSelector = createSelector(pendingSelector, pending => {
    const result = pending['locations'] || pending['locationRates'] || false
    return typeof result !== typeof true ? false : result
})

const clusterSelectorFactory = selector =>
    createSelector(selector, hoveredLocationIdSelector, (selectorOutput, hoveredLocationId) => {
        let locations = []

        if (Array.isArray(selectorOutput)) {
            locations = [...selectorOutput]
        } else if (selectorOutput) {
            locations = [selectorOutput]
        }

        return supercluster({
            radius: 40,
            maxZoom: 15,
            extent: 256,
            reduce: (accumulated, props) => (accumulated.hovered = props.hovered || accumulated.hovered),
            map: ({ id }) => ({
                hovered: hoveredLocationId === id,
            }),
        }).load(locations.map((location, i) => Feature(i, location)))
    })

export const clustersSelector = clusterSelectorFactory(inactiveLocationsSelector)
export const activeLocationClustersSelector = clusterSelectorFactory(activeLocationSelector)
export const clustersPOISelector = clusterSelectorFactory(placesOfInterestSelector)

export const clustersWithLocationsSelector = createDenormalizedSelector(clustersSelector, {})

const BBox = ({ nw, se }) => [nw.lng, se.lat, se.lng, nw.lat]

const pointsSelectorFactory = clustersSelector =>
    createSelector(clustersSelector, boundsSelector, zoomSelector, (clusters, bounds, zoom) =>
        bounds !== null && clusters !== null ? clusters.getClusters(BBox(bounds), zoom) : []
    )

export const inactivePointsSelector = pointsSelectorFactory(clustersSelector)
export const activePointSelector = pointsSelectorFactory(activeLocationClustersSelector)
export const pointsPlacesOfInterestSelector = pointsSelectorFactory(clustersPOISelector)

export const searchParamsSelector = createStructuredSelector({
    searchDateTime: searchDateTimeSelector,
    searchLocation: searchLocationSelector,
    searchMode: searchModeSelector,
    bounds: boundsSelector,
    filters: filtersSelector,
})
